export type FundCategoryKey =
  | "fixed-income"
  | "equity"
  | "market-making"
  | "commodity";

export type FundCategoryConfig = {
  key: FundCategoryKey;
  title: string;
  shortTitle: string;
  path: string;
  colorScheme: string;
  fundRegisterCodes: string[];
  description?: string;
  heroTitle?: string;
  heroHighlight?: string;
  heroSubtitle?: string;
  introTitle?: string;
  introText?: string;
};

export const FUND_PRIORITY_ORDER = [
  "11343",
  "11383",
  "11222",
  "11530",
  "11197",
  "11339",
  "11141",
] as const;

const FUND_PRIORITY_BY_CODE: Map<string, number> = new Map(
  FUND_PRIORITY_ORDER.map((fundRegisterCode, index) => [
    fundRegisterCode,
    index,
  ]),
);

export const sortFundsByPriority = <T extends { fundRegisterCode?: string }>(
  funds: T[],
): T[] =>
  funds
    .map((fund, originalIndex) => ({ fund, originalIndex }))
    .sort((first, second) => {
      const firstPriority =
        FUND_PRIORITY_BY_CODE.get(String(first.fund.fundRegisterCode || "")) ??
        Number.MAX_SAFE_INTEGER;
      const secondPriority =
        FUND_PRIORITY_BY_CODE.get(
          String(second.fund.fundRegisterCode || ""),
        ) ?? Number.MAX_SAFE_INTEGER;

      return firstPriority - secondPriority ||
        first.originalIndex - second.originalIndex;
    })
    .map(({ fund }) => fund);

export const FUND_CATEGORY_CONFIGS: FundCategoryConfig[] = [
  {
    key: "fixed-income",
    title: "صندوق‌های درآمد ثابت",
    shortTitle: "درآمد ثابت",
    path: "/funds/category/fixed-income",
    colorScheme: "green",
    fundRegisterCodes: ["11383", "11343", "11222"],
    description:
      "صندوق‌های درآمد ثابت برای بازدهی پایدار، ریسک پایین و نقدشوندگی مناسب طراحی شده‌اند.",
    heroTitle: "صندوق‌های درآمد ثابت",
    heroHighlight: "تأمین سرمایه امید",
    heroSubtitle: "سرمایه‌گذاری مطمئن با بازدهی پایدار و ریسک پایین",
    introTitle: "معرفی صندوق‌های درآمد ثابت",
    introText:
      "صندوق‌های سرمایه‌گذاری درآمد ثابت ابزارهای مالی مناسب برای سرمایه‌گذارانی هستند که به دنبال درآمد پایدار، ریسک پایین و نقدشوندگی بالا هستند. این صندوق‌ها عمدتاً در اوراق با درآمد ثابت، سپرده‌های بانکی، صکوک و ابزارهای مشابه سرمایه‌گذاری می‌کنند و با مدیریت حرفه‌ای دارایی، بازدهی هدفمند و مطمئن ارائه می‌دهند.",
  },
  {
    key: "equity",
    title: "صندوق‌های سهامی",
    shortTitle: "سهامی",
    path: "/funds/category/equity",
    colorScheme: "brand",
    fundRegisterCodes: ["11197", "11141"],
    description:
      "صندوق‌های سهامی برای سرمایه‌گذارانی مناسب هستند که به دنبال رشد بیشتر سرمایه در افق بلندمدت‌اند.",
    heroTitle: "صندوق‌های سهامی",
    heroHighlight: "تأمین سرمایه امید",
    heroSubtitle: "سرمایه‌گذاری حرفه‌ای در بازار سهام با مدیریت تخصصی",
    introTitle: "معرفی صندوق‌های سهامی",
    introText:
      "صندوق‌های سهامی برای سرمایه‌گذارانی مناسب هستند که ریسک‌پذیری بالاتری دارند و به دنبال بازدهی بیشتر در بلندمدت هستند. این صندوق‌ها عمدتاً در سهام شرکت‌های بورسی و فرابورسی سرمایه‌گذاری می‌کنند و با مدیریت حرفه‌ای پرتفوی، فرصت رشد سرمایه را فراهم می‌آورند.",
  },
  {
    key: "market-making",
    title: "صندوق بازارگردانی",
    shortTitle: "بازارگردانی",
    path: "/funds/category/market-making",
    colorScheme: "orange",
    fundRegisterCodes: ["11339"],
    description:
      "صندوق بازارگردانی برای ایفای نقش تخصصی در نقدشوندگی و پایداری معاملات طراحی شده است.",
    heroTitle: "صندوق بازارگردانی",
    heroHighlight: "تأمین سرمایه امید",
    heroSubtitle: "محصول تخصصی بازار سرمایه با نقش پشتیبان نقدشوندگی",
    introTitle: "معرفی صندوق بازارگردانی",
    introText:
      "صندوق بازارگردانی با هدف بهبود نقدشوندگی، تعادل عرضه و تقاضا و پشتیبانی حرفه‌ای از معاملات در بازار سرمایه فعالیت می‌کند.",
  },
  {
    key: "commodity",
    title: "صندوق کالایی",
    shortTitle: "کالایی",
    path: "/funds/category/commodity",
    colorScheme: "yellow",
    fundRegisterCodes: ["11530"],
    description:
      "صندوق کالایی برای سرمایه‌گذارانی مناسب است که به دنبال دسترسی ساده‌تر به بازار کالا و به ویژه طلا هستند.",
    heroTitle: "صندوق کالایی",
    heroHighlight: "تأمین سرمایه امید",
    heroSubtitle: "سرمایه‌گذاری در دارایی‌های کالایی با دسترسی ساده و شفاف",
    introTitle: "معرفی صندوق کالایی",
    introText:
      "صندوق‌های کالایی امکان سرمایه‌گذاری غیرمستقیم در دارایی‌های مبتنی بر کالا را فراهم می‌کنند و برای افرادی مناسب هستند که به دنبال تنوع‌بخشی، پوشش ریسک و دسترسی آسان‌تر به بازارهایی مانند طلا هستند.",
  },
];

export const FUND_CATEGORY_CONFIG_BY_KEY = Object.fromEntries(
  FUND_CATEGORY_CONFIGS.map((config) => [config.key, config]),
) as Record<FundCategoryKey, FundCategoryConfig>;

export const isVisibleInFundCards = (_fundRegisterCode?: string) => true;

const HIDDEN_DASHBOARD_FUND_REGISTER_CODES = new Set(["11339"]);

export const isVisibleInDashboardFunds = (
  fundRegisterCode?: string,
): boolean =>
  !HIDDEN_DASHBOARD_FUND_REGISTER_CODES.has(
    String(fundRegisterCode || "").trim(),
  );

export const getFundCategoryByRegisterCode = (
  fundRegisterCode?: string,
): FundCategoryConfig | null => {
  const normalizedCode = String(fundRegisterCode || "").trim();

  return (
    FUND_CATEGORY_CONFIGS.find((config) =>
      config.fundRegisterCodes.includes(normalizedCode),
    ) || null
  );
};

export const filterFundsByCategory = <T extends { fundRegisterCode?: string }>(
  funds: T[],
  categoryKey?: FundCategoryKey | "all",
): T[] => {
  if (!Array.isArray(funds)) {
    return [];
  }

  if (!categoryKey || categoryKey === "all") {
    return funds;
  }

  const category = FUND_CATEGORY_CONFIG_BY_KEY[categoryKey];
  if (!category) {
    return funds;
  }

  return funds.filter((fund) =>
    category.fundRegisterCodes.includes(String(fund?.fundRegisterCode || "").trim()),
  );
};
